import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { resolveOperationalLandingPath } from '../../../core/guards/operational-landing.utils';
import { CacheService } from '../../../core/services/cache/cache.service';
import { DataService } from '../../../core/services/data/data.service';
import { LayoutService } from '../../../core/services/layout/layout.service';
import { RoleService } from '../../../core/services/role/role.service';
import { RtoAccessService } from '../../../core/services/rto/rto-access.service';
import { TranslatePipe } from '../../pipes/translate/translate.pipe';
import { ActiveContainerComponent } from '../active-container/active-container';
import { IcBrokerSolidComponent } from '../Icons/ic-broker-solid/ic-broker-solid';

import { IcClientSolidComponent } from '../Icons/ic-client-solid/ic-client-solid';
import { IcCopy } from '../Icons/ic-copy/ic-copy';

import { IcEmcsComponent } from '../Icons/ic-emcs/ic-emcs';
import { IcGlobeComponent } from '../Icons/ic-globe/ic-globe';
import { IcGraphComponent } from '../Icons/ic-graph/ic-graph.component';
import { IcListComponent } from '../Icons/ic-list/ic-list.component';
import { IcParcelBoxComponent } from '../Icons/ic-parcelbox/ic-parcelbox';
import { IcPlaneComponent } from '../Icons/ic-plane/ic-plane';
import { IcWarehouseComponent } from '../Icons/ic-warehouse/ic-warehouse';

import { IcUserRoleSolidComponent } from '../Icons/ic-user-role-solid/ic-user-role-solid';
import { IcUserComponent } from '../Icons/ic-user/ic-user';
import { IcWalletComponent } from '../Icons/ic-wallet/ic-wallet';

import { MatTooltipModule } from '@angular/material/tooltip';
import { EMCS_EU_MOVEMENTS_QUERY_PARAMS } from '../../../configs/navigation.config';
import { UserType } from '../../interfaces';
import {
  EmcsDestinationTypeCode,
  EmcsOriginTypeCode,
} from '../../../pages/emcs/utils/emcs-codebook';
import { AccessibleClickDirective } from '../../directives/accessible-click.directive';
import { IcChevronLeftComponent } from '../Icons/ic-chevron-left/ic-chevron-left';
import { IcChevronRightComponent } from '../Icons/ic-chevron-right/ic-chevron-right';
import { IcCommodityClassifierComponent } from '../Icons/ic-commodity-classifier/ic-commodity-classifier';
import { IcExportComponent } from '../Icons/ic-export/ic-export';
import { IcImportComponent } from '../Icons/ic-import/ic-import';
import { IcInfoComponent } from '../Icons/ic-info/ic-info';
import { IcTransitArrivalComponent } from '../Icons/ic-transit-arrival/ic-transit-arrival';
import { IcTransitDepartureComponent } from '../Icons/ic-transit-departure/ic-transit-departure';
import { IcTransitComponent } from '../Icons/ic-transit/ic-transit';
import { IcWarningComponent } from '../Icons/ic-warning/ic-warning';
import { IcDeclarationTemplate } from '../Icons/settings-icon/ic-declaration-template/ic-declaration-template';
import { IcManiestTemplate } from '../Icons/settings-icon/ic-manifest-template/ic-manifest-template';

/**
 * SidebarComponent component.
 * Provides UI presentation and event handling for sidebar component.
 */
@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslatePipe,
    IcPlaneComponent,
    IcParcelBoxComponent,
    IcEmcsComponent,
    IcWarehouseComponent,
    IcWalletComponent,
    IcGlobeComponent,
    IcUserComponent,
    IcListComponent,
    IcWarningComponent,
    IcGraphComponent,
    IcBrokerSolidComponent,
    IcClientSolidComponent,
    IcUserRoleSolidComponent,
    IcDeclarationTemplate,
    IcManiestTemplate,
    IcCopy,
    IcExportComponent,
    IcImportComponent,
    IcInfoComponent,
    IcTransitComponent,
    ActiveContainerComponent,
    IcTransitArrivalComponent,
    IcTransitDepartureComponent,
    IcCommodityClassifierComponent,
    IcChevronLeftComponent,
    IcChevronRightComponent,
    AccessibleClickDirective,
    MatTooltipModule,
  ],
  templateUrl: './sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent {
  /** Injected service for layout service. */
  public layoutService = inject(LayoutService);
  /** Injected service for data service. */
  private dataService = inject(DataService);
  /** Injected service for cache service. */
  private cacheService = inject(CacheService);
  /** Injected service for router. */
  private router = inject(Router);
  /** Injected service for role. */
  private role = inject(RoleService);
  /** Injected service for rto access service. */
  private rtoAccessService = inject(RtoAccessService);

  /** Property current user. */
  currentUser = this.cacheService.currentUser;
  /** Property active module. */
  activeModule = computed(() => this.dataService.activeModule());

  /** All sidebar modules with optional permission gating. */
  menuItems = computed(() => {
    const all = [
      {
        id: 'import',
        label: 'import',
        icon: 'import',
        route: '/import',
        permission: ['customsShipment.read', 'declaration.read'],
      },
      {
        id: 'export',
        label: 'export',
        icon: 'export',
        route: '/export',
        permission: ['customsShipment.read', 'declaration.read'],
      },
      {
        id: 'transit',
        label: 'transit',
        icon: 'truck',
        route: '/transit-declarations/transit-arrival',
        permission: ['transit.read'],
      },
      {
        id: 'emcs',
        label: 'emcs',
        icon: 'parcelbox',
        route: '/emcs',
        permission: ['emcs.read'],
      },
      {
        id: 'rto',
        label: 'pgts',
        icon: 'warehouse',
        route: '/rto/shipments',
        permission: ['rto.read'],
      },
      {
        id: 'tasks',
        label: 'tasks',
        icon: 'wallet',
        route: '/tickets/tasks',
        permission: ['ticket.read'],
      },
      {
        id: 'settings',
        label: 'globalModules',
        icon: 'globe',
        route: '/settings/brokers',
        permission: [
          'broker.read',
          'client.read',
          'user.read',
          'declarationTemplate.read',
          'manifestTemplate.read',
          'role.read',
        ],
      },
      {
        id: 'dashboard',
        label: 'dashboard',
        icon: 'graph',
        route:
          resolveOperationalLandingPath(
            (permission) => this.role.isPermitted(permission),
            { moduleLandingPage: 'dashboard' }
          ) ?? '/dashboard/shipments',
        permission: [
          'misc.readGlobalStats',
          'customsShipment.stats',
          'customsShipment.read',
          'declaration.read',
          'transit.read',
          'emcs.read',
          'rto.read',
        ],
      },
      {
        id: 'commodity-classifier',
        label: 'commodityClassifier',
        icon: 'commodity-classifier',
        route: '/commodity-classifier',
        permission: ['commodityClassifier.read'],
      },
    ];
    return all.filter((item) => this.isMenuItemPermitted(item));
  });

  /** Property import tabs. */
  importTabs = [
    {
      id: 'shipments',
      label: 'shipments',
      icon: 'ic-plane',
      route: '/import/shipments',
      permission: ['customsShipment.read'],
    },
    {
      id: 'declarations',
      label: 'declarations',
      icon: 'ic-parcelbox',
      route: '/import/declarations',
      permission: ['declaration.read'],
    },
  ];

  /** Property export tabs. */
  exportTabs = [
    {
      id: 'shipments',
      label: 'shipments',
      icon: 'ic-plane',
      route: '/export/shipments',
      permission: ['customsShipment.read'],
    },
    {
      id: 'declarations',
      label: 'declarations',
      icon: 'ic-parcelbox',
      route: '/export/declarations',
      permission: ['declaration.read'],
    },
  ];

  /** Property rto tabs. */
  rtoTabs = [
    {
      id: 'rto-all',
      label: 'allPgtsShipments',
      icon: 'ic-list',
      route: '/rto/shipments',
    },
    {
      id: 'rto-import',
      label: 'importShipments',
      icon: 'ic-import',
      route: '/rto/shipments/import',
    },
    {
      id: 'rto-export',
      label: 'exportShipments',
      icon: 'ic-export-shipment',
      route: '/rto/shipments/export',
    },
  ];

  /** Property dashboard tabs. */
  dashboardTabs = [
    {
      id: 'shipments',
      label: 'shipmentsDashboard',
      icon: 'ic-plane',
      route: '/dashboard/shipments',
      permission: [
        'customsShipment.read',
        'customsShipment.stats',
        'misc.readGlobalStats',
      ],
    },
    {
      id: 'declarations',
      label: 'declarationsDashboard',
      icon: 'ic-parcelbox',
      route: '/dashboard/declarations',
      permission: ['declaration.read'],
    },
    {
      id: 'transit',
      label: 'transitDashboard',
      icon: 'ic-transit',
      route: '/dashboard/transit',
      permission: ['transit.read'],
    },
    {
      id: 'emcs',
      label: 'emcsDashboard',
      icon: 'ic-emcs',
      route: '/dashboard/emcs',
      permission: ['emcs.read'],
    },
    {
      id: 'rto',
      label: 'pgtsDashboard',
      icon: 'ic-warehouse',
      route: '/dashboard/rto',
      permission: ['rto.read'],
    },
    {
      id: 'tickets',
      label: 'tasksDashboard',
      icon: 'ic-wallet',
      route: '/dashboard/tickets',
      permission: ['ticket.read'],
    },
    {
      id: 'settings',
      label: 'settingsDashboard',
      icon: 'ic-globe',
      route: '/dashboard/settings',
      permission: [
        'broker.read',
        'client.read',
        'user.read',
        'declarationTemplate.read',
        'manifestTemplate.read',
        'role.read',
      ],
    },
  ];

  /** Property transit tabs. */
  transitTabs = [
    /* {
      id: 'overview',
      label: 'overview',
      icon: 'ic-info',
      route: '/transit-declarations/overview',
    }, */
    {
      id: 'transit-arrival',
      label: 'transitArrival',
      icon: 'transit-arrival-icon',
      route: '/transit-declarations/transit-arrival',
    },
    {
      id: 'transit-departure',
      label: 'transitDeparture',
      icon: 'transit-departure-icon',
      route: '/transit-declarations/transit-departure',
    },
  ];

  /** Property emcs tabs. */
  emcsTabs = [
    {
      id: 'emcs-list',
      label: 'euMovements',
      icon: 'ic-list',
      route: '/emcs',
      queryParams: { ...EMCS_EU_MOVEMENTS_QUERY_PARAMS },
    },
    {
      id: 'emcs-import',
      label: 'import',
      icon: 'ic-import',
      route: '/emcs',
      queryParams: {
        specificValueFields: JSON.stringify({
          originTypeCode: EmcsOriginTypeCode.Import,
        }),
      },
    },
    {
      id: 'emcs-export',
      label: 'export',
      icon: 'ic-export-shipment',
      route: '/emcs',
      queryParams: {
        specificValueFields: JSON.stringify({
          destinationTypeCode: EmcsDestinationTypeCode.Export,
        }),
      },
    },
  ];

  /** Property declaration tabs. */
  declarationTabs = [
    {
      id: 'import-declaration',
      label: 'importDeclarations',
      icon: 'ic-import',
      route: '/declarations/import',
    },
    {
      id: 'export-declaration',
      label: 'exportDeclarations',
      icon: 'ic-export-declaration',
      route: '/declarations/export',
    },
  ];

  /** Property ticket tabs. */
  ticketTabs = [
    { id: 'tasks', label: 'tasks', icon: 'ic-list', route: '/tickets/tasks' },
  ];

  /** Property settings tabs. */
  settingsTabs = [
    {
      id: '1',
      label: 'brokers',
      icon: 'ic-broker',
      route: '/settings/brokers',
      permission: ['broker.read'],
    },
    {
      id: '2',
      label: 'clients',
      icon: 'ic-client',
      route: '/settings/clients',
      permission: ['client.read'],
    },
    {
      id: '3',
      label: 'parties',
      icon: 'ic-parties-solid',
      route: '/settings/parties',
      permission: ['party.read'],
    },
    {
      id: '4',
      label: 'templates',
      icon: 'ic-declaration-template',
      route: '/settings/declaration-templates',
      permission: ['declarationTemplate.read', 'manifestTemplate.read'],
    },
    {
      id: '5',
      label: 'roles',
      icon: 'ic-user-role',
      route: '/settings/roles',
      permission: ['role.read'],
    },
    {
      id: '6',
      label: 'users',
      icon: 'ic-user',
      route: '/settings/users',
      permission: ['user.read'],
    },
  ];

  /** Settings sub-tabs filtered by the current user's permissions. */
  filteredSettingsTabs = computed(() => {
    const userType = this.currentUser()?.userType;
    const hideRoles =
      userType === UserType.broker || userType === UserType.client;
    return this.settingsTabs
      .filter((tab) => tab.label !== 'roles' || !hideRoles)
      .filter((tab) => this.isMenuItemPermitted(tab));
  });

  /** Property active module tabs. */
  activeModuleTabs = computed(() => {
    const mod = this.activeModule();
    if (mod === 'import') {
      return this.importTabs.filter((tab) => this.isMenuItemPermitted(tab));
    }
    if (mod === 'export') {
      return this.exportTabs.filter((tab) => this.isMenuItemPermitted(tab));
    }
    if (mod === 'transit') return this.transitTabs;
    if (mod === 'emcs') return this.emcsTabs;
    if (mod === 'rto') return this.rtoTabs;
    if (mod === 'tasks') return this.ticketTabs;
    if (mod === 'settings') return this.filteredSettingsTabs();
    if (mod === 'dashboard') {
      return this.dashboardTabs.filter((tab) => this.isMenuItemPermitted(tab));
    }
    return [];
  });

  /**
   * Layout classes for a module row (expanded card vs collapsed square).
   * @param moduleId - Sidebar module id
   * @returns Space-separated class list for the module link
   */
  moduleLinkClass(moduleId: string): string {
    const active = this.activeModule() === moduleId;
    const collapsed = this.layoutService.isSidebarCollapsed();
    const base =
      'cursor-pointer transition-all duration-300 flex items-center overflow-hidden';

    if (collapsed) {
      const square = `${base} w-9 h-9 shrink-0 rounded-md justify-center`;
      return active
        ? `${square} bg-white/50 shadow-card`
        : `${square} hover:bg-bg-soft`;
    }

    const expanded = `${base} w-full gap-xs px-xs`;
    return active
      ? `${expanded} h-12 rounded-lg bg-white/50 shadow-card sidebar-module-link--active`
      : `${expanded} h-11 hover:bg-bg-soft rounded-lg`;
  }

  /**
   * Handles on module click logic.
   * @param moduleId - The module id
   * @param route - The route
   */
  onModuleClick(moduleId: string, route: string) {
    if (moduleId === 'rto' && !this.role.isRtoEnabledForCurrentUser()) {
      this.rtoAccessService.openRtoNotConfiguredModal();
      return;
    }
    this.dataService.setActiveModule(moduleId);
    if (moduleId === 'emcs') {
      const euTab = this.emcsTabs.find((t) => t.id === 'emcs-list');
      this.router.navigate([route], {
        queryParams: euTab?.queryParams ?? {},
      });
      return;
    }
    this.router.navigate([route]);
  }

  /**
   * Handles on tab click logic.
   * @param route - The route
   * @param queryParams - The query params
   */
  onTabClick(route: string, queryParams?: Record<string, string>): void {
    if (
      !route.startsWith('/dashboard') &&
      (route.startsWith('/rto') || route.includes('rto')) &&
      !this.role.isRtoEnabledForCurrentUser()
    ) {
      this.rtoAccessService.openRtoNotConfiguredModal();
      return;
    }
    this.router.navigate([route], {
      queryParams: queryParams ?? {},
    });
  }

  /**
   * Handles is tab active logic.
   * @param route - The route
   * @param queryParams - The query params
   */
  isTabActive(route: string, queryParams?: Record<string, string>): boolean {
    // Strip both query parameters (?) and matrix parameters (;)
    let currentUrl = this.router.url.split('?')[0].split(';')[0];
    // Remove trailing slash for comparison
    if (currentUrl.endsWith('/') && currentUrl.length > 1) {
      currentUrl = currentUrl.slice(0, -1);
    }
    let normalizedRoute = route.split('?')[0].split(';')[0];
    if (normalizedRoute.endsWith('/') && normalizedRoute.length > 1) {
      normalizedRoute = normalizedRoute.slice(0, -1);
    }

    const routeMatches =
      normalizedRoute.endsWith('/all') ||
      normalizedRoute === '/declarations' ||
      normalizedRoute === '/tickets/tasks'
        ? currentUrl === normalizedRoute ||
          currentUrl === normalizedRoute.replace('/all', '')
        : currentUrl.startsWith(normalizedRoute);

    // Declaration Templates owns nested manifest create/edit when opened with parent id.
    if (
      !routeMatches &&
      normalizedRoute === '/settings/declaration-templates' &&
      currentUrl.startsWith('/settings/manifest-templates')
    ) {
      const urlQuery = this.router.parseUrl(this.router.url).queryParams;
      if (urlQuery['declarationTemplateId']) {
        return true;
      }
    }

    if (!routeMatches) return false;

    // EMCS Import/Export share `/emcs` — match on specificValueFields when provided.
    if (queryParams !== undefined) {
      const urlQuery = this.router.parseUrl(this.router.url).queryParams;
      const currentSpecific = urlQuery['specificValueFields'] ?? undefined;
      const tabSpecific = queryParams['specificValueFields'];
      const currentNot = urlQuery['specificNotFields'] ?? undefined;
      const tabNot = queryParams['specificNotFields'];

      if (tabSpecific) {
        return currentSpecific === tabSpecific;
      }
      if (tabNot) {
        return !currentSpecific && currentNot === tabNot;
      }
      return !currentSpecific && !currentNot;
    }

    return true;
  }

  /**
   * Checks whether a sidebar menu item or tab is permitted for the current user.
   * Items without a `permission` field are always visible.
   * @param item - Menu item or tab with an optional `permission` field
   * @returns True if the item should be shown
   */
  private isMenuItemPermitted(item: {
    permission?: string | string[];
  }): boolean {
    if (!item.permission) {
      return true;
    }
    return this.role.isPermitted(item.permission);
  }
}
